import { useToast } from "vue-toastification";
import Swal from "sweetalert2/dist/sweetalert2.esm.all.js";

export function useNotification() {
    const toast = useToast();

    const showSuccess = (message) => {
        toast.success(message, {
            timeout: 3000,
            closeOnClick: true,
            pauseOnFocusLoss: true,
            pauseOnHover: true,
            draggable: true,
            draggablePercent: 0.6,
            showCloseButtonOnHover: false,
            hideProgressBar: false,
            closeButton: "button",
            icon: true,
            rtl: false
        });
    };

    const showError = (message) => {
        toast.error(message, {
            timeout: 5000,
            closeOnClick: true,
            pauseOnFocusLoss: true,
            pauseOnHover: true,
            draggable: true,
            draggablePercent: 0.6,
            showCloseButtonOnHover: false,
            hideProgressBar: false,
            closeButton: "button",
            icon: true,
            rtl: false
        });
    };

    const showWarning = (message) => {
        toast.warning(message, {
            timeout: 4000,
            closeOnClick: true,
            pauseOnFocusLoss: true,
            pauseOnHover: true,
            draggable: true,
            draggablePercent: 0.6,
            showCloseButtonOnHover: false,
            hideProgressBar: false,
            closeButton: "button",
            icon: true,
            rtl: false
        });
    };

    const showInfo = (message) => {
        toast.info(message, {
            timeout: 3000,
            closeOnClick: true,
            pauseOnFocusLoss: true,
            pauseOnHover: true,
            draggable: true,
            draggablePercent: 0.6,
            showCloseButtonOnHover: false,
            hideProgressBar: false,
            closeButton: "button",
            icon: true,
            rtl: false
        });
    };

    const confirmAction = async (title, text, confirmButtonText = "ยืนยัน", cancelButtonText = "ยกเลิก") => {
        try {
            const result = await Swal.fire({
                title: title,
                text: text,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#855E42",
                cancelButtonColor: "#6b7280",
                confirmButtonText: confirmButtonText,
                cancelButtonText: cancelButtonText,
                reverseButtons: true
            });
            return result.isConfirmed;
        } catch (e) {
            console.warn("SweetAlert2 error, using native confirm", e);
            return window.confirm(`${title}\n\n${text}`);
        }
    };

    const confirmDelete = async (itemName = "รายการนี้") => {
        try {
            const result = await Swal.fire({
                title: "ยืนยันการลบ?",
                text: `คุณต้องการลบ${itemName}ใช่หรือไม่? การกระทำนี้ไม่สามารถย้อนกลับได้`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#dc2626",
                cancelButtonColor: "#6b7280",
                confirmButtonText: "ลบ",
                cancelButtonText: "ยกเลิก",
                reverseButtons: true
            });
            return result.isConfirmed;
        } catch (e) {
            console.warn("SweetAlert2 error, using native confirm", e);
            return window.confirm(`ยืนยันการลบ?\n\nคุณต้องการลบ${itemName}ใช่หรือไม่? การกระทำนี้ไม่สามารถย้อนกลับได้`);
        }
    };

    const showSuccessAlert = async (title, text = "") => {
        try {
            await Swal.fire({
                title: title,
                text: text,
                icon: "success",
                confirmButtonColor: "#855E42",
                confirmButtonText: "ตกลง"
            });
        } catch (e) {
            console.warn("SweetAlert2 error, using native alert", e);
            window.alert(`${title}\n\n${text}`);
        }
    };

    const showErrorAlert = async (title, text = "") => {
        try {
            await Swal.fire({
                title: title,
                text: text,
                icon: "error",
                confirmButtonColor: "#855E42",
                confirmButtonText: "ตกลง"
            });
        } catch (e) {
            console.warn("SweetAlert2 error, using native alert", e);
            window.alert(`${title}\n\n${text}`);
        }
    };

    const handleApiError = (error, setErrors = null) => {
        if (error.response) {
            const apiErrors = error.response.data?.errors;
            
            if (error.response.status === 422 && setErrors) {
                if (apiErrors) {
                    const formattedErrors = {};
                    for (const key in apiErrors) {
                        formattedErrors[key] = apiErrors[key][0];
                    }
                    setErrors(formattedErrors);
                    showError("กรุณาตรวจสอบข้อมูลให้ถูกต้อง");
                }
            } else if (error.response.status === 401) {
                // ถ้ามี errors object (เช่น login ผิด) ให้แสดงที่ field
                if (apiErrors && setErrors) {
                    const formattedErrors = {};
                    for (const key in apiErrors) {
                        formattedErrors[key] = apiErrors[key][0];
                    }
                    setErrors(formattedErrors);
                    showError(error.response.data.message || "ข้อมูลไม่ถูกต้อง");
                } else {
                    showError("กรุณาเข้าสู่ระบบใหม่");
                }
            } else if (error.response.status === 403) {
                showError("คุณไม่มีสิทธิ์ในการดำเนินการนี้");
            } else if (error.response.status === 404) {
                showError("ไม่พบข้อมูลที่ต้องการ");
            } else if (error.response.status === 500) {
                showError("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์");
            } else {
                showError(error.response.data.message || "เกิดข้อผิดพลาด");
            }
        } else if (error.request) {
            showError("ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้");
        } else {
            console.error(error);
            showError("เกิดข้อผิดพลาดที่ไม่คาดคิด");
        }
    };

    return {
        showSuccess,
        showError,
        showWarning,
        showInfo,
        confirmAction,
        confirmDelete,
        showSuccessAlert,
        showErrorAlert,
        handleApiError
    };
}
